import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  Tooltip,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  AccountBalanceWallet as LedgerIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Layout from '../layout/Layout';
import { getCurrentUser } from '../../services/authService';
import { supplierService } from '../../services/supplierService';
import axiosInstance from '../../utils/axios';
import API_CONFIG from '../../config/api.config';

const Suppliers = () => {
  const navigate = useNavigate();
  const user = getCurrentUser();
  const companyId = user?.companyId || 1;

  // State management
  const [suppliers, setSuppliers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierToDelete, setSupplierToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchField, setSearchField] = useState('name');
  const [formValues, setFormValues] = useState({
    name: '',
    supplier_code: '',
    contact_person: '',
    email: '',
    phone: '',
    gst_number: '',
    state_code: '',
    website: '',
    address: '',
    notes: '',
    company_id: companyId
  });
  const [gstStates, setGstStates] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [errors, setErrors] = useState({});
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuSupplier, setActionMenuSupplier] = useState(null);

  // Load suppliers
  const loadSuppliers = async () => {
    try {
      const response = await supplierService.getAllSuppliers();
      // Handle paginated response
      setSuppliers(response.content || []);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to load suppliers',
        severity: 'error'
      });
    }
  };

  useEffect(() => {
    loadSuppliers();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get(API_CONFIG.ENDPOINTS.STATES);
        if (!cancelled && Array.isArray(res.data)) {
          setGstStates(res.data);
        }
      } catch (e) {
        console.error('Failed to load GST states', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let v = value;
    if (name === 'phone') {
      v = value.replace(/\D/g, '').slice(0, 15);
      
      // Perform duplicate check in background
      if (v.length >= 7) {
        const digits = v;
        (async () => {
          try {
            // Company-wise duplicate phone check against loaded suppliers
            const conflict = Array.isArray(suppliers) && suppliers.some(s =>
              (s.companyId === companyId || s.company_id === companyId) &&
              (s.phone || '').replace(/\D/g, '') === digits &&
              (!selectedSupplier || s.id !== selectedSupplier.id)
            );
            if (conflict) {
              setErrors(prev => ({ ...prev, phone: 'Phone already exists for this company' }));
            } else {
              const exists = await supplierService.existsPhone(digits);
              if (exists && (!selectedSupplier || (selectedSupplier.phone || '').replace(/\D/g, '') !== digits)) {
                setErrors(prev => ({ ...prev, phone: 'Phone already exists for this company' }));
              }
            }
          } catch (error) {
            console.error('Error checking duplicate phone:', error);
          }
        })();
      }
    }
    if (name === 'email') {
      const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
      if (v && !pattern.test(v)) {
        setErrors(prev => ({ ...prev, email: 'Invalid email format' }));
      }
    }
    setFormValues(prev => ({
      ...prev,
      [name]: v
    }));
    // Clear error for this field when user starts typing (only if not a conflict error we just set)
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Name is required
    if (!formValues.name || formValues.name.trim() === '') {
      newErrors.name = 'Name is required';
    }

    // Phone is required
    if (!formValues.phone || formValues.phone.trim() === '') {
      newErrors.phone = 'Phone is required';
    }
    // Phone length limit
    if (formValues.phone && formValues.phone.replace(/\D/g, '').length > 15) {
      newErrors.phone = 'Phone must be at most 15 digits';
    }
    // Company-wise duplicate phone check
    if (formValues.phone) {
      const digits = (formValues.phone || '').replace(/\D/g, '');
      const conflict = Array.isArray(suppliers) && suppliers.some(s =>
        (s.companyId === companyId || s.company_id === companyId) &&
        (s.phone || '').replace(/\D/g, '') === digits &&
        (!selectedSupplier || s.id !== selectedSupplier.id)
      );
      if (conflict) {
        newErrors.phone = 'Phone already exists for this company';
      }
    }
    // Email format validation
    if (formValues.email && formValues.email.trim() !== '') {
      const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
      if (!pattern.test(formValues.email)) {
        newErrors.email = 'Invalid email format';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Filter suppliers based on search
  const filteredSuppliers = Array.isArray(suppliers) ? suppliers.filter(supplier => {
    const searchValue = searchQuery.toLowerCase();
    const fieldValue = supplier[searchField]?.toLowerCase() || '';
    return fieldValue.includes(searchValue);
  }) : [];

  // Dialog handlers
  const handleOpenDialog = (supplier = null) => {
    // Clear errors when opening dialog
    setErrors({});

    if (supplier) {
      setSelectedSupplier(supplier);
      setFormValues({
        name: supplier.name || '',
        supplier_code: supplier.supplierCode || '',
        contact_person: supplier.contactPerson || '',
        email: supplier.email || '',
        phone: supplier.phone || '',
        gst_number: supplier.gstNumber || '',
        state_code: supplier.state?.stateCode || '',
        website: supplier.website || '',
        address: supplier.address || '',
        notes: supplier.notes || '',
        company_id: companyId
      });
    } else {
      setSelectedSupplier(null);
      setFormValues({
        name: '',
        supplier_code: '',
        contact_person: '',
        email: '',
        phone: '',
        gst_number: '',
        state_code: '',
        website: '',
        address: '',
        notes: '',
        company_id: companyId
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedSupplier(null);
  };

  // Delete dialog handlers
  const handleOpenDeleteDialog = (supplier) => {
    setSupplierToDelete(supplier);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSupplierToDelete(null);
  };

  // Form submission handler
  const handleSubmit = async () => {
    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    try {
      console.log('Submitting supplier data:', formValues);

      const supplierData = {
        name: formValues.name.trim(),
        supplierCode: formValues.supplier_code ? formValues.supplier_code.trim() : null,
        email: formValues.email.trim(),
        phone: formValues.phone.trim(),
        gstNumber: formValues.gst_number?.trim() || null,
        stateCode: formValues.state_code || null,
        contactPerson: formValues.contact_person.trim(),
        address: formValues.address.trim(),
        notes: formValues.notes?.trim() || null,
        website: formValues.website?.trim() || null,
        companyId: user?.companyId || 1
      };

      console.log('Sending to API:', supplierData);

      if (selectedSupplier) {
        await supplierService.updateSupplier(selectedSupplier.id, supplierData);
        setSnackbar({
          open: true,
          message: 'Supplier updated successfully',
          severity: 'success'
        });
      } else {
        await supplierService.createSupplier(supplierData);
        setSnackbar({
          open: true,
          message: 'Supplier created successfully',
          severity: 'success'
        });
      }

      handleCloseDialog();
      loadSuppliers();
    } catch (error) {
      console.error('Error submitting supplier:', error);
      // If backend reports duplicate phone, show error on field and snackbar
      if (error.response?.status === 409) {
        setErrors(prev => ({ ...prev, phone: error.response?.data?.message || 'Phone already exists for this company' }));
      }
      setSnackbar({
        open: true,
        message: `Failed to ${selectedSupplier ? 'update' : 'create'} supplier: ${error.response?.data?.message || error.message}`,
        severity: 'error'
      });
    }
  };

  // Delete handler
  const handleDeleteSupplier = async () => {
    try {
      await supplierService.deleteSupplier(supplierToDelete.id);
      setSnackbar({
        open: true,
        message: 'Supplier deleted successfully',
        severity: 'success'
      });
      handleCloseDeleteDialog();
      loadSuppliers();
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Failed to delete supplier: ${error.response?.data?.message || error.message}`,
        severity: 'error'
      });
    }
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Snackbar handler
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleActionMenuOpen = (event, supplier) => {
    event.stopPropagation();
    setActionMenuAnchor(event.currentTarget);
    setActionMenuSupplier(supplier);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setActionMenuSupplier(null);
  };

  const handleActionEdit = (supplier) => {
    handleActionMenuClose();
    handleOpenDialog(supplier);
  };

  const handleActionDelete = (supplier) => {
    handleActionMenuClose();
    handleOpenDeleteDialog(supplier);
  };

  const handleActionViewLedger = (supplier) => {
    handleActionMenuClose();
    if (supplier?.id) {
      navigate(`/supplier-ledger/${supplier.id}`);
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 2 }}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5">
              Suppliers Management
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Supplier
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder={`Search by ${searchField}`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
            <Button
              variant="outlined"
              onClick={() => setSearchField(searchField === 'name' ? 'email' : 'name')}
            >
              Search by: {searchField === 'name' ? 'Name' : 'Email'}
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>S.No</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Contact Person</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>GST</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell>Website</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSuppliers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((supplier, index) => (
                    <TableRow key={supplier.id}>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>{supplier.supplierCode || '-'}</TableCell>
                      <TableCell>{supplier.name}</TableCell>
                      <TableCell>{supplier.contactPerson}</TableCell>
                      <TableCell>{supplier.email}</TableCell>
                      <TableCell>{supplier.phone}</TableCell>
                      <TableCell>{supplier.gstNumber || '-'}</TableCell>
                      <TableCell>{supplier.state?.name ? `${supplier.state.stateCode} — ${supplier.state.name}` : '-'}</TableCell>
                      <TableCell>{supplier.website || '-'}</TableCell>
                      <TableCell>{supplier.address}</TableCell>
                      <TableCell>{supplier.notes || '-'}</TableCell>
                      <TableCell>
                        <Tooltip title="Actions">
                          <IconButton
                            size="small"
                            onClick={(e) => handleActionMenuOpen(e, supplier)}
                            aria-label="Actions"
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredSuppliers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>

        <Menu
          anchorEl={actionMenuAnchor}
          open={Boolean(actionMenuAnchor)}
          onClose={handleActionMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={() => handleActionEdit(actionMenuSupplier)}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
          </MenuItem>
          <MenuItem onClick={() => handleActionViewLedger(actionMenuSupplier)}>
            <LedgerIcon fontSize="small" sx={{ mr: 1 }} /> View Ledger
          </MenuItem>
          <MenuItem onClick={() => handleActionDelete(actionMenuSupplier)}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
          </MenuItem>
        </Menu>

        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {selectedSupplier ? 'Edit Supplier' : 'Add New Supplier'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={formValues.name}
                onChange={handleInputChange}
                required
                error={!!errors.name}
                helperText={errors.name}
                inputProps={{ maxLength: 100 }}
              />
              <TextField
                fullWidth
                label="Supplier Code"
                name="supplier_code"
                value={formValues.supplier_code}
                onChange={handleInputChange}
                inputProps={{ maxLength: 50 }}
                helperText="Leave empty to auto-generate (e.g., TSTSUP1234)"
              />
              <TextField
                fullWidth
                label="Contact Person"
                name="contact_person"
                value={formValues.contact_person}
                onChange={handleInputChange}
                inputProps={{ maxLength: 100 }}
              />
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formValues.email}
                onChange={handleInputChange}
                error={!!errors.email}
                helperText={errors.email}
                inputProps={{ maxLength: 100 }}
              />
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={formValues.phone}
                onChange={handleInputChange}
                required
                error={!!errors.phone}
                helperText={errors.phone}
                inputProps={{ maxLength: 15 }}
              />
              <TextField
                fullWidth
                label="GST Number"
                name="gst_number"
                value={formValues.gst_number}
                onChange={handleInputChange}
                inputProps={{ maxLength: 30 }}
              />
              <FormControl fullWidth>
                <InputLabel id="supplier-state-label">State (GST)</InputLabel>
                <Select
                  labelId="supplier-state-label"
                  name="state_code"
                  value={formValues.state_code || ''}
                  label="State (GST)"
                  onChange={handleInputChange}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {gstStates.map((s) => (
                    <MenuItem key={s.stateCode} value={s.stateCode}>
                      {s.stateCode} — {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Website"
                name="website"
                value={formValues.website}
                onChange={handleInputChange}
                inputProps={{ maxLength: 255 }}
              />
              <TextField
                fullWidth
                label="Address"
                name="address"
                multiline
                rows={2}
                value={formValues.address}
                onChange={handleInputChange}
                inputProps={{ maxLength: 255 }}
              />
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                multiline
                rows={2}
                value={formValues.notes}
                onChange={handleInputChange}
                inputProps={{ maxLength: 255 }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained" color="primary">
              {selectedSupplier ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={deleteDialogOpen}
          onClose={handleCloseDeleteDialog}
        >
          <DialogTitle>Delete Supplier</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this supplier? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
            <Button onClick={handleDeleteSupplier} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default Suppliers; 
